import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, CalendarDays, CheckCheck, Clock3, MessageSquare,
  RefreshCw, CircleHelp, AlertCircle, ChevronRight, Inbox,
} from "lucide-react";
import ManagerLayout from "../Layouts/ManagerLayout";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const number = (value) => value == null ? "—" : value.toLocaleString("en-IN");
const percent = (part, total) => total ? Math.round((part / total) * 100) : 0;
const statusTone = (status = "") => {
  switch (status.toLowerCase()) {
    case "completed": case "solved": return "green";
    case "confirmed": return "blue";
    case "cancelled": return "gray";
    case "feedback": return "purple";
    default: return "amber";
  }
};

const toneClasses = {
  blue: "bg-blue-50 text-blue-700 ring-blue-100",
  amber: "bg-amber-50 text-amber-700 ring-amber-100",
  purple: "bg-indigo-50 text-indigo-600 ring-indigo-100",
  green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  gray: "bg-slate-100 text-slate-500 ring-slate-200",
};
const barClasses = {
  blue: "bg-blue-500",
  amber: "bg-amber-400",
  purple: "bg-indigo-500",
  green: "bg-emerald-500",
  gray: "bg-slate-400",
};
const metricThemes = {
  blue: {
    icon: "bg-blue-100 text-blue-600",
    card: "shadow-[0_4px_14px_-5px_rgba(59,130,246,0.3)] hover:border-blue-200 hover:shadow-[0_6px_20px_-4px_rgba(59,130,246,0.4)]",
  },
  amber: {
    icon: "bg-amber-100 text-amber-600",
    card: "shadow-[0_4px_14px_-5px_rgba(245,158,11,0.3)] hover:border-amber-200 hover:shadow-[0_6px_20px_-4px_rgba(245,158,11,0.4)]",
  },
  purple: {
    icon: "bg-purple-100 text-purple-600",
    card: "shadow-[0_4px_14px_-5px_rgba(168,85,247,0.3)] hover:border-purple-200 hover:shadow-[0_6px_20px_-4px_rgba(168,85,247,0.4)]",
  },
  rose: {
    icon: "bg-rose-100 text-rose-600",
    card: "shadow-[0_4px_14px_-5px_rgba(244,63,94,0.3)] hover:border-rose-200 hover:shadow-[0_6px_20px_-4px_rgba(244,63,94,0.4)]",
  },
};
const focusClasses = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-400";
const panelClasses = "min-w-0 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_2px_8px_-4px_rgba(15,23,42,0.08)]";

function PanelHeading({ id, title, description, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-5 sm:px-6">
      <div>
        <h2 id={id} className="text-base font-bold tracking-tight text-slate-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function Skeleton({ className }) {
  return <div className={`animate-pulse rounded-lg bg-slate-100 motion-reduce:animate-none ${className}`} />;
}

function EmptyState({ icon: Icon = CalendarDays, title, children }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-8 text-center">
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-400 ring-1 ring-slate-100">
        <Icon size={23} strokeWidth={1.5} aria-hidden="true" />
      </span>
      <strong className="text-sm font-semibold text-slate-800">{title}</strong>
      <p className="mt-1.5 max-w-[270px] text-sm leading-relaxed text-slate-500">{children}</p>
    </div>
  );
}

function Breakdown({ rows, total }) {
  return (
    <div className="space-y-4">
      {rows.map(({ label, count, tone }) => (
        <div key={label}>
          <div className="mb-2 flex items-center justify-between gap-3 text-sm">
            <span className="inline-flex items-center text-slate-600">
              <i className={`mr-2 h-2 w-2 shrink-0 rounded-full ${barClasses[tone]}`} aria-hidden="true" />
              {label}
            </span>
            <strong className="font-semibold tabular-nums text-slate-900">{number(count)}</strong>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <span className={`block h-full rounded-full ${barClasses[tone]}`} style={{ width: `${percent(count, total)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ManagerDash() {
  const [stats, setStats] = useState(null);
  const [queries, setQueries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const name = localStorage.getItem("name");

  useEffect(() => {
    const controller = new AbortController();
    const token = localStorage.getItem("token");
    if (!name || !token) {
      setErrors(["Your session is incomplete. Please sign in again to load your dashboard."]);
      setLoading(false);
      return () => controller.abort();
    }

    async function loadDashboard() {
      setLoading(true);
      setErrors([]);
      setStats(null);
      setQueries(null);
      setUpdatedAt(null);
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: { name, role: "manager" },
        signal: controller.signal,
      };
      const [appointmentsResult, queriesResult] = await Promise.allSettled([
        axios.get(`${backendUrl}/manager/dashboard-stats`, config),
        axios.get(`${backendUrl}/manager/query-summary`, config),
      ]);
      if (controller.signal.aborted) return;

      const failures = [];
      if (appointmentsResult.status === "fulfilled" && appointmentsResult.value.data?.stats) {
        setStats(appointmentsResult.value.data.stats);
      } else {
        failures.push("Appointments could not be loaded.");
      }
      if (queriesResult.status === "fulfilled" && queriesResult.value.data?.data) {
        setQueries(queriesResult.value.data.data);
      } else {
        failures.push("Query summary could not be loaded.");
      }
      setErrors(failures);
      if (failures.length < 2) setUpdatedAt(new Date());
      setLoading(false);
    }
    loadDashboard();
    return () => controller.abort();
  }, [name, refreshKey]);

  const appointments = stats?.appointments;
  const recentAppointments = stats?.recentAppointments || [];
  const completionRate = percent(appointments?.completed, appointments?.total);
  const resolutionRate = percent(queries?.solved, queries?.total);
  const metrics = [
    { label: "Total appointments", value: appointments?.total, icon: CalendarDays, tone: "blue", route: "/manager-appointments" },
    { label: "Pending appointments", value: appointments?.pending, icon: Clock3, tone: "amber", route: "/manager-appointments" },
    { label: "Total queries", value: queries?.total, icon: MessageSquare, tone: "purple", route: "/query-dash" },
    { label: "Pending queries", value: queries?.pending, icon: CircleHelp, tone: "rose", route: "/query-dash" },
  ];
  const appointmentRows = [
    { label: "Completed", count: appointments?.completed || 0, tone: "green" },
    { label: "In progress", count: appointments?.pending || 0, tone: "blue" },
    { label: "Cancelled", count: appointments?.cancelled || 0, tone: "gray" },
  ];
  const dateLabel = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" });

  return (
    <ManagerLayout>
      <div className="mx-auto min-w-0 max-w-[1600px] space-y-6 pb-4 text-slate-800" aria-busy={loading}>
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-400">
              <span>Workspace</span><ChevronRight size={12} aria-hidden="true" /><span className="text-slate-600">Overview</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950">Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="mr-1 hidden items-center gap-2 text-sm text-slate-500 sm:inline-flex">
              <CalendarDays size={16} aria-hidden="true" />{dateLabel}
            </span>
            <button
              className={`inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60 motion-reduce:transition-none ${focusClasses}`}
              disabled={loading}
              onClick={() => setRefreshKey((key) => key + 1)}
            >
              <RefreshCw size={15} className={loading ? "animate-spin motion-reduce:animate-none" : ""} aria-hidden="true" />
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </header>

        {errors.length > 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" role="alert">
            <AlertCircle size={20} className="shrink-0" aria-hidden="true" />
            <div>
              {errors.map((error) => <p className="mb-1 font-semibold" key={error}>{error}</p>)}
              <span className="text-xs">Use Refresh to try again.</span>
            </div>
          </div>
        )}

        <section className="grid grid-cols-1 gap-4 min-[520px]:grid-cols-2 min-[1200px]:grid-cols-4" aria-label="Work summary">
          {metrics.map(({ label, value, icon: Icon, tone, route }) => (
            <Link
              className={`group relative flex min-h-[100px] items-center gap-4 rounded-xl border border-slate-200/80 bg-white px-4 py-5 transition-[border-color,box-shadow] duration-200 motion-reduce:transition-none ${metricThemes[tone].card} ${focusClasses}`}
              to={route}
              key={label}
            >
              <ArrowUpRight size={15} className="absolute right-3 top-3 text-slate-400 transition-colors group-hover:text-slate-700" aria-hidden="true" />
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${metricThemes[tone].icon}`}>
                <Icon size={23} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-normal leading-5 text-slate-500">{label}</h2>
                {loading ? <Skeleton className="mt-1 h-6 w-12" /> : <strong className="mt-0.5 block text-xl font-semibold leading-6 tabular-nums text-slate-800">{number(value)}</strong>}
              </div>
            </Link>
          ))}
        </section>

        <div className="grid grid-cols-1 items-start gap-6 min-[1200px]:grid-cols-[minmax(0,1fr)_320px] min-[1500px]:grid-cols-[minmax(0,1fr)_360px]">
          <section className={panelClasses} aria-labelledby="md-recent-heading">
            <PanelHeading id="md-recent-heading" title="Recent appointments" description="Your latest assigned appointments">
              <Link className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 ${focusClasses}`} to="/manager-appointments">
                View all<ArrowRight size={14} aria-hidden="true" />
              </Link>
            </PanelHeading>
            {loading ? (
              <div className="space-y-4 p-6">{[1, 2, 3].map((key) => <Skeleton key={key} className="h-16" />)}</div>
            ) : !appointments ? (
              <EmptyState title="Recent appointments unavailable">Refresh to try loading your appointments again.</EmptyState>
            ) : recentAppointments.length === 0 ? (
              <EmptyState title="No recent appointments">New appointments will appear here when they are assigned to you.</EmptyState>
            ) : (
              <div className="max-w-full overflow-x-auto">
                <table className="w-full min-w-[590px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/80 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 [&>th]:px-5 [&>th]:py-3.5">
                      <th>Customer</th><th>Reference</th><th>Slot</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentAppointments.map((appointment) => (
                      <tr
                        key={appointment._id || appointment.appointmentId}
                        className="border-b border-slate-100 text-sm text-slate-600 transition-colors last:border-b-0 hover:bg-slate-50/70 [&>td]:max-w-[260px] [&>td]:px-5 [&>td]:py-3 [&>td]:[overflow-wrap:anywhere]"
                      >
                        <td>
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-600 ring-2 ring-blue-50/40" aria-hidden="true">
                              {(appointment.name || "?").trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()}
                            </span>
                            <div className="min-w-0">
                              <strong className="block font-semibold text-slate-800">{appointment.name || "—"}</strong>
                              <span className="mt-0.5 block text-xs text-slate-400">{appointment.subject || "—"}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="font-medium text-slate-700">AP{appointment.appointmentId}</span>
                          <small className="mt-0.5 block text-xs text-slate-400">{appointment.enrollment || appointment.platform || "—"}</small>
                        </td>
                        <td className="text-xs leading-relaxed">{appointment.slot || "Not scheduled"}</td>
                        <td>
                          <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ring-1 ring-inset ${toneClasses[statusTone(appointment.status)]}`}>
                            <i className={`h-1.5 w-1.5 rounded-full ${barClasses[statusTone(appointment.status)]}`} aria-hidden="true" />
                            {appointment.status || "Pending"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/40 px-5 py-3.5 text-xs text-slate-400 sm:px-6">
              <span>{loading ? "Loading appointments…" : appointments ? `Showing ${recentAppointments.length} most recent` : "Appointments unavailable"}</span>
              {updatedAt && <span>Updated {updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>}
            </div>
          </section>

          <aside className="grid min-w-0 grid-cols-1 gap-6 min-[700px]:grid-cols-2 min-[1200px]:grid-cols-1" aria-label="Work insights">
            <section className={panelClasses} aria-labelledby="md-appointments-heading">
              <PanelHeading id="md-appointments-heading" title="Appointment overview">
                <span className="rounded-md bg-slate-50 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">All time</span>
              </PanelHeading>
              {loading ? (
                <div className="p-6"><Skeleton className="h-36" /></div>
              ) : !appointments ? (
                <EmptyState title="Appointment overview unavailable">Refresh to try loading your appointments again.</EmptyState>
              ) : !appointments.total ? (
                <EmptyState title="No appointments yet">Your assigned appointments will appear here.</EmptyState>
              ) : (
                <div className="p-5 sm:p-6">
                  <div className="mb-3 flex items-baseline justify-between gap-2">
                    <span className="text-xs text-slate-500">Completion rate</span>
                    <strong className="text-lg font-semibold tabular-nums text-slate-900">{completionRate}%</strong>
                  </div>
                  <div className="mb-6 h-2.5 overflow-hidden rounded-full bg-slate-100" role="img" aria-label={`${completionRate}% of appointments completed`}>
                    <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${completionRate}%` }} />
                  </div>
                  <div className="space-y-3">
                    {appointmentRows.map(({ label, count, tone }) => (
                      <div key={label} className="flex items-center justify-between text-sm">
                        <span className="inline-flex items-center gap-2.5 text-slate-500"><i className={`h-2 w-2 rounded-full ${barClasses[tone]}`} aria-hidden="true" />{label}</span>
                        <span className="font-semibold tabular-nums text-slate-800">{number(count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className={panelClasses} aria-labelledby="md-query-heading">
              <PanelHeading id="md-query-heading" title="Query overview">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500"><MessageSquare size={15} aria-hidden="true" /></span>
              </PanelHeading>
              {loading ? (
                <div className="p-6"><Skeleton className="h-36" /></div>
              ) : !queries ? (
                <EmptyState icon={Inbox} title="Query overview unavailable">Refresh to try loading your queries again.</EmptyState>
              ) : !queries.total ? (
                <EmptyState icon={Inbox} title="No queries yet">Your inbox is clear. New queries will appear here.</EmptyState>
              ) : (
                <div className="p-5 sm:p-6">
                  <div className="mb-5 flex items-center justify-between rounded-xl bg-emerald-50 px-3 py-2.5">
                    <span className="inline-flex items-center gap-2 text-xs font-medium text-emerald-700"><CheckCheck size={16} aria-hidden="true" />Resolution rate</span>
                    <strong className="text-sm font-bold text-emerald-700">{resolutionRate}%</strong>
                  </div>
                  <Breakdown total={queries.total} rows={[
                    { label: "New", count: queries.pending, tone: "amber" },
                    { label: "Solved", count: queries.solved, tone: "green" },
                    { label: "Feedback", count: queries.feedback, tone: "purple" },
                  ]} />
                </div>
              )}
              <Link className={`flex items-center justify-between border-t border-slate-100 bg-slate-50/40 px-5 py-3.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50/50 sm:px-6 ${focusClasses}`} to="/query-dash">
                View query dashboard<ArrowRight size={14} aria-hidden="true" />
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </ManagerLayout>
  );
}

export default ManagerDash;
